import { getRequestConfig, withMakeRequest } from '~/api';
import Request from '~/api/Request.ts';
import {
  PaginationBaseRequest,
  PaginationBaseResponse
} from '~/api/types/pagination.ts';
import { ConnectedAppProvider } from '~/api/types/connectedAppProviders.ts';
import { ConnectionData } from '~/api/types/connectionData.ts';
import { ConnectedAccountData } from '~/api/types/connectedAccounts.ts';
import { AgentConnectToolData } from '~/api/types/agentConnectTools.ts';
import camelcaseKeys from 'camelcase-keys';

type GetConnectedProvidersRequest = PaginationBaseRequest & {
  identifier?: string;
};

type GetConnectedProvidersResponse = PaginationBaseResponse & {
  providers: ConnectedAppProvider[];
};

const getConnectedAppProviders: (
  request: GetConnectedProvidersRequest
) => Promise<GetConnectedProvidersResponse> = withMakeRequest(
  async ({ pageSize, pageToken, identifier }: GetConnectedProvidersRequest) =>
    await Request.getInstance().get(
      `/providers?page_token=${pageToken ?? ''}&page_size=${pageSize}${identifier ? `&identifier=${identifier}` : ''}`,
      getRequestConfig({ addEnvDomain: true })
    )
);

export type GetConnectedAppListRequest = PaginationBaseRequest & {
  provider?: string;
};

export type GetConnectedAppListResponse = PaginationBaseResponse & {
  connections: ConnectionData[];
};

const getConnectedAppList: (
  request: GetConnectedAppListRequest
) => Promise<GetConnectedAppListResponse> = withMakeRequest(
  async ({ pageToken, pageSize, provider }: GetConnectedAppListRequest) =>
    await Request.getInstance().get(
      `/connections/app?page_token=${pageToken}&page_size=${pageSize}&${provider ? `provider=${provider}` : ''}`,
      getRequestConfig({ addEnvDomain: true })
    )
);

export type GetConnectedAccountListRequest = PaginationBaseRequest & {
  connector?: string;
  search?: string;
};

export type GetConnectedAccountListResponse = PaginationBaseResponse & {
  connectedAccounts: ConnectedAccountData[];
};

const getConnectedAccountList: (
  request: GetConnectedAccountListRequest
) => Promise<GetConnectedAccountListResponse> = withMakeRequest(
  async ({
    pageToken,
    pageSize,
    connector,
    search
  }: GetConnectedAccountListRequest) => {
    return await Request.getInstance().get(
      `/connected_accounts?page_size=${pageSize}${pageToken ? `&page_token=${pageToken}` : ''}${connector ? `&connector=${connector}` : ''}${search ? `&query=${search}` : ''}`,
      getRequestConfig({ addEnvDomain: true })
    );
  }
);

type CreateConnectedAccountMagicLinkRequest = {
  identifier: string;
  connector: string;
};

export type CreateConnectedAccountMagicLinkResponse = {
  expiry: string;
  link: string;
};

const createConnectedAccountMagicLink: (
  request: CreateConnectedAccountMagicLinkRequest
) => Promise<CreateConnectedAccountMagicLinkResponse> = withMakeRequest(
  async (data: CreateConnectedAccountMagicLinkRequest) =>
    await Request.getInstance().post(
      `/connected_accounts/magic_link`,
      data,
      getRequestConfig({ addEnvDomain: true })
    )
);

const createAgentPortalRedirectUrl: () => Promise<string> = withMakeRequest(
  async () =>
    await Request.getInstance().get(
      `/connected_accounts/portal/redirect_url`,
      {}
    ),
  (data: { redirectUrl: string }) => data.redirectUrl
);

type CreateConnectedAccountRequest = {
  identifier: string;
  connector: string;
  authDetails?: Record<string, any>;
  apiConfig?: Record<string, any>;
};

type CreateConnectedAccountResponse = {
  connectedAccount: ConnectedAccountData;
};

const createConnectedAccount: (
  request: CreateConnectedAccountRequest
) => Promise<ConnectedAccountData> = withMakeRequest(
  async ({
    identifier,
    connector,
    authDetails,
    apiConfig
  }: CreateConnectedAccountRequest) =>
    await Request.getInstance().post(
      `/connected_accounts`,
      {
        identifier,
        connector,
        connectedAccount: {
          ...(authDetails && { authorizationDetails: authDetails }),
          ...(apiConfig && { apiConfig })
        }
      },
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: CreateConnectedAccountResponse) => data.connectedAccount
);

type UpdateConnectedAccountRequest = {
  identifier: string;
  connector: string;
  authDetails: Record<string, any>;
  apiConfig?: Record<string, any>;
};

type UpdateConnectedAccountResponse = {
  connectedAccount: ConnectedAccountData;
};

const updateConnectedAccount: (
  request: UpdateConnectedAccountRequest
) => Promise<ConnectedAccountData> = withMakeRequest(
  async ({
    identifier,
    connector,
    authDetails,
    apiConfig
  }: CreateConnectedAccountRequest) =>
    await Request.getInstance().put(
      `/connected_accounts`,
      {
        identifier,
        connector,
        connectedAccount: {
          ...(authDetails && { authorizationDetails: authDetails }),
          ...(apiConfig && { apiConfig })
        }
      },
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: UpdateConnectedAccountResponse) => data.connectedAccount
);

type GetConnectedAccountDetailsByIdentifierRequest = {
  identifier: string;
  connector: string;
};

type GetConnectedAccountDetailsByIdentifierResponse = {
  connectedAccount: ConnectedAccountData;
};

const getConnectedAccountDetailsByIdentifier: (
  request: GetConnectedAccountDetailsByIdentifierRequest
) => Promise<ConnectedAccountData> = withMakeRequest(
  async ({
    identifier,
    connector
  }: GetConnectedAccountDetailsByIdentifierRequest) =>
    await Request.getInstance().get(
      `/connected_accounts/auth?connector=${connector}&identifier=${encodeURIComponent(identifier)}`,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: GetConnectedAccountDetailsByIdentifierResponse) =>
    data.connectedAccount
);

type DeleteConnectedAccountByIdentifierRequest = {
  account: ConnectedAccountData;
};

const deleteConnectedAccountByIdentifier: (
  request: DeleteConnectedAccountByIdentifierRequest
) => Promise<boolean> = withMakeRequest(
  async ({
    account: { identifier, connector }
  }: DeleteConnectedAccountByIdentifierRequest) =>
    await Request.getInstance().post(
      `/connected_accounts:delete`,
      {
        identifier,
        connector
      },
      getRequestConfig({ addEnvDomain: true })
    ),
  () => true
);

type GetToolsListRequest = PaginationBaseRequest & {
  filters?: {
    provider?: string;
    toolName?: string;
    summary?: boolean;
  };
};

type GetToolsListResponse = PaginationBaseResponse & {
  toolNames: string[];
  tools: AgentConnectToolData[];
};

const getToolsListApiFilterParams = (
  request: GetToolsListRequest
): URLSearchParams => {
  const params = new URLSearchParams();
  if (request.pageToken) {
    params.append('page_token', request.pageToken);
  }
  if (request.pageSize) {
    params.append('page_size', request.pageSize.toString());
  }
  if (request.filters?.provider) {
    params.append('filter.provider', request.filters.provider);
  }
  if (request.filters?.toolName) {
    params.append('filter.tool_name', request.filters.toolName);
  }
  if (request.filters?.summary) {
    params.append('filter.summary', 'true');
  }
  return params;
};

const getToolsList: (
  request: GetToolsListRequest
) => Promise<GetToolsListResponse> = withMakeRequest(
  async (request: GetToolsListRequest) =>
    await Request.getInstance().get(
      `/tools?${getToolsListApiFilterParams(request).toString()}`,
      getRequestConfig({ addEnvDomain: true, camelCaseResponse: false })
    ),
  (data: Record<string, any>) => {
    data.tools.forEach((tool) => {
      Object.keys(tool.definition.input_schema.properties).forEach(
        (fieldName) => {
          const field = tool.definition.input_schema.properties[fieldName];
          // Ensure the field has a name property
          if (field && typeof field === 'object' && !field.name) {
            field.name = fieldName; // Assign the field name if not present
          }
        }
      );
    });
    return camelcaseKeys(data, { deep: true }) as GetToolsListResponse;
  }
);

type ExecuteToolRequest = {
  tool_name: string; // already snake cased as we dont want to do it as part of the interceptor
  identifier?: string;
  connectedAccountId?: string;
  params: Record<string, any>;
};

type ExecuteToolResponse = Record<any, any>;

const executeTool: (
  request: ExecuteToolRequest
) => Promise<ExecuteToolResponse> = withMakeRequest(
  async (data: ExecuteToolRequest) =>
    await Request.getInstance().post(
      `/execute_tool`,
      data,
      getRequestConfig({
        addEnvDomain: true,
        snakeCaseRequest: false,
        camelCaseResponse: false
      })
    )
);

type UpdateConnectedAccountInAgentPortalRequest = Omit<
  UpdateConnectedAccountRequest,
  'identifier'
>;

const updateConnectedAccountInAgentPortal: (
  request: UpdateConnectedAccountInAgentPortalRequest
) => Promise<ConnectedAccountData> = withMakeRequest(
  async ({
    connector,
    authDetails,
    apiConfig
  }: CreateConnectedAccountRequest) =>
    await Request.getInstance().put(
      `/connected_accounts`,
      {
        connector,
        connectedAccount: {
          ...(authDetails && { authorizationDetails: authDetails }),
          ...(apiConfig && { apiConfig })
        }
      },
      {}
    ),
  (data: UpdateConnectedAccountResponse) => data.connectedAccount
);

const getConnectedAccountDetailsInAgentPortal: () => Promise<ConnectedAccountData> =
  withMakeRequest(
    async () => await Request.getInstance().get(`/connected_accounts/-`, {}),
    (data: GetConnectedAccountDetailsByIdentifierResponse) =>
      data.connectedAccount
  );

const disconnectConnectedAccountInAgentPortal: () => Promise<ConnectedAccountData> =
  withMakeRequest(
    async () =>
      await Request.getInstance().post(`/connected_accounts/-/disconnect`),
    (data: GetConnectedAccountDetailsByIdentifierResponse) =>
      data.connectedAccount
  );

export {
  getConnectedAppProviders,
  getConnectedAppList,
  getConnectedAccountList,
  createConnectedAccountMagicLink,
  getConnectedAccountDetailsByIdentifier,
  createConnectedAccount,
  updateConnectedAccount,
  deleteConnectedAccountByIdentifier,
  getToolsList,
  executeTool,
  createAgentPortalRedirectUrl,
  updateConnectedAccountInAgentPortal,
  getConnectedAccountDetailsInAgentPortal,
  disconnectConnectedAccountInAgentPortal
};
